import { useState, useEffect, useCallback } from 'react';
import { aliasAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, Upload, Download, Plus, Trash2, Loader2, BookOpen, Sparkles, Check, X, Pencil } from 'lucide-react';

const PAGE_SIZE = 50;

export default function AliasCatalogPage() {
  const [aliases, setAliases] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ item_code: '', category: '', canonical_name: '', alias: '', alias_type: '', confidence: 'High', notes: '' });
  const [uploading, setUploading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestBusyId, setSuggestBusyId] = useState(null);
  const [selectedAi, setSelectedAi] = useState(new Set());
  const [selectedBuyer, setSelectedBuyer] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    try { const r = await aliasAPI.listSuggestions('pending'); setSuggestions(r.data.suggestions || []); }
    catch (e) { /* silent */ }
  }, []);

  const aiSuggestions = suggestions.filter(s => s.type !== 'buyer_correction');
  const buyerSuggestions = suggestions.filter(s => s.type === 'buyer_correction');

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const handleGenerateSuggestions = async () => {
    setSuggestLoading(true);
    try {
      const r = await aliasAPI.suggestPartNumbers();
      if (!r.data.job_id) { toast.info(r.data.message || 'Nothing new to suggest'); setSuggestLoading(false); return; }
      toast.info(`AI checking ${r.data.total} items — this can take a few minutes...`);
      const poll = setInterval(async () => {
        try {
          const status = await aliasAPI.getSuggestStatus(r.data.job_id);
          if (status.data.status === 'completed') {
            clearInterval(poll); setSuggestLoading(false);
            toast.success(`${status.data.suggested || 0} new suggestion(s) ready for review`);
            fetchSuggestions();
          } else if (status.data.status === 'failed') {
            clearInterval(poll); setSuggestLoading(false);
            toast.error(`Suggestion generation failed: ${status.data.error || 'Unknown error'}`);
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
      setTimeout(() => { clearInterval(poll); setSuggestLoading(false); }, 900000); // 15 min ceiling
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start'); setSuggestLoading(false); }
  };

  const handleApproveSuggestion = async (id) => {
    setSuggestBusyId(id);
    try { await aliasAPI.approveSuggestion(id); toast.success('Added to alias catalog'); setSuggestions(s => s.filter(x => x.id !== id)); fetchData(); }
    catch (e) { toast.error('Failed to approve'); }
    finally { setSuggestBusyId(null); }
  };

  const handleRejectSuggestion = async (id) => {
    setSuggestBusyId(id);
    try { await aliasAPI.rejectSuggestion(id); toast.info('Rejected'); setSuggestions(s => s.filter(x => x.id !== id)); }
    catch (e) { toast.error('Failed to reject'); }
    finally { setSuggestBusyId(null); }
  };

  const toggleSelect = (setFn, id) => setFn(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleBulkApprove = async (ids, clearFn) => {
    if (!ids.size) return;
    setBulkBusy(true);
    try {
      await aliasAPI.bulkApproveSuggestions([...ids]);
      toast.success(`Approved ${ids.size} alias(es)`);
      setSuggestions(s => s.filter(x => !ids.has(x.id))); clearFn(new Set()); fetchData();
    } catch (e) { toast.error('Bulk approve failed'); } finally { setBulkBusy(false); }
  };

  const handleBulkReject = async (ids, clearFn) => {
    if (!ids.size) return;
    setBulkBusy(true);
    try {
      await aliasAPI.bulkRejectSuggestions([...ids]);
      toast.info(`Rejected ${ids.size} alias(es)`);
      setSuggestions(s => s.filter(x => !ids.has(x.id))); clearFn(new Set());
    } catch (e) { toast.error('Bulk reject failed'); } finally { setBulkBusy(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (catFilter !== 'all') params.category = catFilter;
      const r = await aliasAPI.list(params);
      setAliases(r.data.aliases || []);
      setTotal(r.data.total || 0);
      setCategories(r.data.categories || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [page, search, catFilter]);

  useEffect(() => { setPage(0); }, [search, catFilter]);
  useEffect(() => { const t = setTimeout(fetchData, 300); return () => clearTimeout(t); }, [fetchData]);

  const handleAdd = async () => {
    if (!form.alias || !form.canonical_name) { toast.error('Alias and Canonical Name required'); return; }
    try { await aliasAPI.add(form); toast.success('Alias added'); setAddOpen(false); setForm({ item_code: '', category: '', canonical_name: '', alias: '', alias_type: '', confidence: 'High', notes: '' }); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (alias) => {
    try { await aliasAPI.remove(alias); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const r = await aliasAPI.importFile(file); toast.success(r.data.message); fetchData(); }
    catch (err) { toast.error('Import failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6" data-testid="alias-catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><BookOpen className="h-6 w-6 text-primary" />Master Alias Catalog</h1>
          <p className="text-muted-foreground text-sm">{total} aliases across {categories.length} categories</p>
        </div>
        <div className="flex gap-2">
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} className="hidden" id="alias-upload" />
          <Button variant="outline" onClick={() => document.getElementById('alias-upload').click()} disabled={uploading} className="rounded-sm">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Import Excel
          </Button>
          <Button variant="outline" onClick={() => window.open(aliasAPI.exportUrl(), '_blank')} className="rounded-sm"><Download className="h-4 w-4 mr-2" />Export</Button>
          <Button variant="outline" onClick={handleGenerateSuggestions} disabled={suggestLoading} className="rounded-sm" data-testid="generate-suggestions-btn">
            {suggestLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}AI-Suggest Part Numbers
          </Button>
          <Button onClick={() => setAddOpen(true)} className="rounded-sm"><Plus className="h-4 w-4 mr-2" />Add Alias</Button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <Card className="rounded-sm border-blue-200" data-testid="alias-suggestions-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <h2 className="text-sm font-semibold">AI-Suggested Part Numbers ({aiSuggestions.length} pending review)</h2>
              </div>
              {selectedAi.size > 0 && (
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" disabled={bulkBusy} onClick={() => handleBulkReject(selectedAi, setSelectedAi)} data-testid="bulk-reject-ai-btn">Reject Selected ({selectedAi.size})</Button>
                  <Button size="sm" className="h-7 rounded-sm text-xs" disabled={bulkBusy} onClick={() => handleBulkApprove(selectedAi, setSelectedAi)} data-testid="bulk-approve-ai-btn">Approve Selected ({selectedAi.size})</Button>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">These items only have a short stepping/die code as their SKU. AI found a likely full manufacturer part number for each — review and Approve or Reject; nothing is added to the live catalog until you decide.</p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {aiSuggestions.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 p-3 border rounded-sm" data-testid={`suggestion-row-${s.id}`}>
                  <input type="checkbox" className="h-4 w-4 shrink-0" checked={selectedAi.has(s.id)} onChange={() => toggleSelect(setSelectedAi, s.id)} data-testid={`select-suggestion-${s.id}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.canonical_name}</p>
                    <p className="text-xs text-muted-foreground">SKU: <span className="font-mono">{s.sku}</span> → Suggested: <span className="font-mono text-blue-700">{s.suggested_part_number}</span></p>
                    {s.reasoning && <p className="text-xs text-muted-foreground mt-0.5">{s.reasoning}</p>}
                  </div>
                  <Badge variant="outline" className={`text-xs rounded-sm shrink-0 ${s.confidence >= 70 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{s.confidence}%</Badge>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 px-2 rounded-sm" disabled={suggestBusyId === s.id} onClick={() => handleRejectSuggestion(s.id)} data-testid={`reject-suggestion-${s.id}`}><X className="h-3.5 w-3.5 text-destructive" /></Button>
                    <Button size="sm" className="h-7 px-2 rounded-sm" disabled={suggestBusyId === s.id} onClick={() => handleApproveSuggestion(s.id)} data-testid={`approve-suggestion-${s.id}`}><Check className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {buyerSuggestions.length > 0 && (
        <Card className="rounded-sm border-violet-200" data-testid="alias-buyer-corrections-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Pencil className="h-4 w-4 text-violet-600" />
                <h2 className="text-sm font-semibold">Learned Aliases from Bid Engine ({buyerSuggestions.length} pending review)</h2>
              </div>
              {selectedBuyer.size > 0 && (
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" disabled={bulkBusy} onClick={() => handleBulkReject(selectedBuyer, setSelectedBuyer)} data-testid="bulk-reject-buyer-btn">Reject Selected ({selectedBuyer.size})</Button>
                  <Button size="sm" className="h-7 rounded-sm text-xs" disabled={bulkBusy} onClick={() => handleBulkApprove(selectedBuyer, setSelectedBuyer)} data-testid="bulk-approve-buyer-btn">Approve Selected ({selectedBuyer.size})</Button>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">A buyer manually corrected a match in the Bid Engine. Approve to add this alias to the master catalog so it auto-matches next time, or reject to discard it.</p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {buyerSuggestions.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 p-3 border rounded-sm" data-testid={`buyer-suggestion-row-${s.id}`}>
                  <input type="checkbox" className="h-4 w-4 shrink-0" checked={selectedBuyer.has(s.id)} onChange={() => toggleSelect(setSelectedBuyer, s.id)} data-testid={`select-buyer-suggestion-${s.id}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{s.canonical_name} <span className="text-xs text-muted-foreground font-mono">({s.item_code || s.sku})</span></p>
                    <p className="text-xs text-muted-foreground">Vendor text: <span className="font-mono">"{s.alias_original}"</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">Learned from: {s.learned_from_user || 'Unknown buyer'}</p>
                  </div>
                  <Badge variant="outline" className="text-xs rounded-sm shrink-0 bg-violet-50 text-violet-700">{s.confidence}</Badge>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 px-2 rounded-sm" disabled={suggestBusyId === s.id} onClick={() => handleRejectSuggestion(s.id)} data-testid={`reject-buyer-suggestion-${s.id}`}><X className="h-3.5 w-3.5 text-destructive" /></Button>
                    <Button size="sm" className="h-7 px-2 rounded-sm" disabled={suggestBusyId === s.id} onClick={() => handleApproveSuggestion(s.id)} data-testid={`approve-buyer-suggestion-${s.id}`}><Check className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm"><CardContent className="p-4">
        <div className="flex gap-4">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search aliases, products, codes..." className="pl-10 rounded-sm" /></div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-44 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card className="rounded-sm"><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-muted/50">
            <TableHead className="text-xs font-medium">Item Code</TableHead>
            <TableHead className="text-xs font-medium">Category</TableHead>
            <TableHead className="text-xs font-medium">Canonical Name</TableHead>
            <TableHead className="text-xs font-medium">Alias / Variant</TableHead>
            <TableHead className="text-xs font-medium">Type</TableHead>
            <TableHead className="text-xs font-medium">Confidence</TableHead>
            <TableHead className="text-xs font-medium">Approved By</TableHead>
            <TableHead className="text-xs font-medium text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></TableCell></TableRow>
            : aliases.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No aliases found</TableCell></TableRow>
            : aliases.map((a, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{a.item_code}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs rounded-sm">{a.category}</Badge></TableCell>
                <TableCell className="text-sm font-medium">{a.canonical_name}</TableCell>
                <TableCell className="text-sm">{a.alias_original}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{a.alias_type}</TableCell>
                <TableCell><Badge variant="outline" className={`text-xs rounded-sm ${a.confidence === 'High' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{a.confidence}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {a.approved_by_name ? <span data-testid={`approved-by-${i}`}>{a.approved_by_name}<br /><span className="text-[10px]">{new Date(a.approved_at).toLocaleDateString()}</span></span> : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(a.alias)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {total > PAGE_SIZE && <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Previous</Button>
          <span className="flex items-center px-3 text-sm">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next</Button>
        </div>
      </div>}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Alias</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Item Code</Label><Input value={form.item_code} onChange={e => setForm(p => ({ ...p, item_code: e.target.value }))} /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} /></div>
            </div>
            <div><Label>Canonical Product Name *</Label><Input value={form.canonical_name} onChange={e => setForm(p => ({ ...p, canonical_name: e.target.value }))} /></div>
            <div><Label>Alias / Variant *</Label><Input value={form.alias} onChange={e => setForm(p => ({ ...p, alias: e.target.value }))} placeholder="The name vendors use" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label><Input value={form.alias_type} onChange={e => setForm(p => ({ ...p, alias_type: e.target.value }))} placeholder="Acronym, Typo, etc." /></div>
              <div><Label>Confidence</Label><Select value={form.confidence} onValueChange={v => setForm(p => ({ ...p, confidence: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent></Select></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd}>Add Alias</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
